import { motion } from 'framer-motion';
import { Instagram } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('/imagenes/sitio/hero-banner.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/80 via-[#0B0B0B]/70 to-[#0B0B0B]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-7xl font-light text-[#F5F5F5] mb-6 tracking-tight leading-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="hero-title"
        >
          {t.hero.title1}
          <br />
          <span className="font-normal italic text-gold-400 gold-glow">{t.hero.title2}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-base sm:text-lg lg:text-xl text-[#9CA3AF] mb-12 max-w-3xl mx-auto tracking-wide leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <motion.button
            onClick={() => scrollToSection('portfolio')}
            whileHover={{ scale: 1.05, backgroundColor: '#FFFFFF', color: '#0B0B0B' }}
            whileTap={{ scale: 0.95 }}
            className="bg-gold-400 text-[#0B0B0B] px-8 py-3.5 text-xs sm:text-sm uppercase tracking-[0.16em] font-semibold transition-all w-full sm:w-auto shadow-lg shadow-gold-400/10"
            data-testid="hero-btn-portfolio"
          >
            {t.hero.btnPortfolio}
          </motion.button>

          <motion.button
            onClick={() => scrollToSection('contacto')}
            whileHover={{ scale: 1.05, borderColor: '#FFFFFF', color: '#FFFFFF' }}
            whileTap={{ scale: 0.95 }}
            className="border border-gold-400/40 text-[#E5E5E5] px-8 py-3.5 text-xs sm:text-sm uppercase tracking-[0.16em] font-medium transition-all w-full sm:w-auto"
            data-testid="hero-btn-consulta"
          >
            {t.hero.btnConsulta}
          </motion.button>
        </motion.div>

        {/* Instagram Link */}
        <motion.a
          href="https://www.instagram.com/ramiro_alfonsot/"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          whileHover={{ scale: 1.1 }}
          className="inline-flex items-center gap-2 mt-12 text-[#9CA3AF] hover:text-[#FFFFFF] transition-colors"
          data-testid="hero-instagram-link"
        >
          <Instagram size={20} />
          <span className="text-sm tracking-wide">@ramiro_alfonsot</span>
        </motion.a>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 1,
          delay: 1,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-[#E5E5E5] rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#E5E5E5] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};
